import { useMemo } from "react";
import { useScopedWhere } from "#app/widgets/dashboards/scope/use-scoped-where";
import { resolveWidgetFilters } from "#app/widgets/dashboards/widget-filters";
import { useIssueGroupCounts } from "#app/hooks/issue-list/use-issue-group-counts";

/** What a metric card counts. `total` = every project in scope; `unassigned` =
 * projects with no assignee. */
export type MetricMeasure = "total" | "unassigned";

// `total` groups by stage (1:1 with a project) so summing every bucket — including
// the null `__unassigned__` bucket — is the exact project count without the
// double-counting a multi-assignee `groupBy` would introduce. `unassigned` groups
// by assignee and reads only the `__unassigned__` bucket.
const GROUP_BY: Record<MetricMeasure, string> = {
  total: "stageId",
  unassigned: "assignees.entityId",
};

/**
 * The metric card's data layer: folds the ambient dashboard window + the card's
 * own Filters blob into a `where` (via `useScopedWhere`, exactly like
 * `useDimensionSlices`), then reduces the grouped counts to one number.
 */
export function useMetric(
  measure: MetricMeasure,
  filters: unknown,
  dateField: "creationDate" | "lastUpdateDate" = "creationDate",
) {
  const { base, override } = resolveWidgetFilters(filters);
  const { where } = useScopedWhere(base, { dateField, override });
  const { counts, ready, error } = useIssueGroupCounts({ where, groupBy: GROUP_BY[measure] });

  const value = useMemo(() => {
    if (measure === "unassigned") return counts["__unassigned__"] ?? 0;
    return Object.values(counts).reduce((sum, n) => sum + n, 0);
  }, [counts, measure]);

  return { value, loading: !ready, error };
}
